export type WelcomeAgeGateMinAge = 18 | 21;

export type WelcomeAgeGateCopy = {
  heading: string;
  body: string;
  confirmText: string;
};

export function normalizeWelcomeAgeGateMinAge(raw: unknown): WelcomeAgeGateMinAge {
  const n = typeof raw === "string" ? parseInt(raw, 10) : Number(raw);
  return n === 18 ? 18 : 21;
}

export function defaultWelcomeAgeGateCopy(
  minAge: WelcomeAgeGateMinAge,
): WelcomeAgeGateCopy {
  return {
    heading: `Are You Over ${minAge}?`,
    body: "Please confirm your age before proceeding",
    confirmText: `I confirm that I am ${minAge} years of age or older.`,
  };
}

function clip(value: unknown, max: number): string {
  return String(value || "").trim().slice(0, max);
}

export function sanitizeWelcomeAgeGateCopy(
  raw: unknown,
): WelcomeAgeGateCopy {
  const src = raw && typeof raw === "object"
    ? raw as { heading?: unknown; body?: unknown; confirmText?: unknown }
    : {};
  return {
    heading: clip(src.heading, 80),
    body: clip(src.body, 240),
    confirmText: clip(src.confirmText, 160),
  };
}

export function resolveWelcomeAgeGateCopy(
  minAge: WelcomeAgeGateMinAge,
  raw: unknown,
): WelcomeAgeGateCopy {
  const defaults = defaultWelcomeAgeGateCopy(minAge);
  const custom = sanitizeWelcomeAgeGateCopy(raw);
  return {
    heading: custom.heading || defaults.heading,
    body: custom.body || defaults.body,
    confirmText: custom.confirmText || defaults.confirmText,
  };
}

/** Inclusive ages: 14.0 may continue; 18.0 is adult/standard flow. */
export const UNDER18_FLOW_MIN_AGE = 14;
export const UNDER18_FLOW_ADULT_AGE = 18;

export type Under18FlowAgeBand = "teen" | "adult";

export type Under18FlowCopy = {
  heading: string;
  body: string;
  under14Error: string;
};

export function defaultUnder18FlowCopy(): Under18FlowCopy {
  return {
    heading: "Enter your date of birth",
    body: "Please confirm your age before proceeding",
    under14Error: "You must be 14 or older to continue.",
  };
}

export function sanitizeUnder18FlowCopy(raw: unknown): Under18FlowCopy {
  const src = raw && typeof raw === "object"
    ? raw as { heading?: unknown; body?: unknown; under14Error?: unknown }
    : {};
  return {
    heading: clip(src.heading, 80),
    body: clip(src.body, 240),
    under14Error: clip(src.under14Error, 160),
  };
}

export function resolveUnder18FlowCopy(raw: unknown): Under18FlowCopy {
  const defaults = defaultUnder18FlowCopy();
  const custom = sanitizeUnder18FlowCopy(raw);
  return {
    heading: custom.heading || defaults.heading,
    body: custom.body || defaults.body,
    under14Error: custom.under14Error || defaults.under14Error,
  };
}

/** Age in whole years on today's local calendar date. Null if the DOB is invalid. */
export function computeAgeYearsFromDob(
  month: string,
  day: string,
  year: string,
): number | null {
  const monthNum = parseInt(month, 10);
  const dayNum = parseInt(day, 10);
  const yearNum = parseInt(year, 10);

  if (!month || !day || !year) return null;
  if (monthNum < 1 || monthNum > 12) return null;
  if (dayNum < 1 || dayNum > 31) return null;
  if (yearNum < 1900 || yearNum > new Date().getFullYear()) return null;

  const birthDate = new Date(yearNum, monthNum - 1, dayNum);
  if (
    birthDate.getFullYear() !== yearNum
    || birthDate.getMonth() !== monthNum - 1
    || birthDate.getDate() !== dayNum
  ) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
}

export function classifyUnder18FlowAge(age: number): Under18FlowAgeBand | "blocked" {
  if (age < UNDER18_FLOW_MIN_AGE) return "blocked";
  if (age < UNDER18_FLOW_ADULT_AGE) return "teen";
  return "adult";
}

const under18FlowBandStorageKey = (hostPrefix: string) => (
  `momento_under18_flow_band_${hostPrefix}`
);

export function readUnder18FlowAgeBand(hostPrefix: string): Under18FlowAgeBand | null {
  try {
    const raw = sessionStorage.getItem(under18FlowBandStorageKey(hostPrefix));
    if (raw === "teen" || raw === "adult") return raw;
  } catch {
    /* private mode */
  }
  return null;
}

export function setUnder18FlowAgeBand(
  hostPrefix: string,
  band: Under18FlowAgeBand,
): void {
  try {
    sessionStorage.setItem(under18FlowBandStorageKey(hostPrefix), band);
  } catch {
    /* ignore */
  }
}

export function isUnder18FlowTeenSession(): boolean {
  try {
    const hostPrefix = window.location.hostname.split(".")[0].toLowerCase();
    return readUnder18FlowAgeBand(hostPrefix) === "teen";
  } catch {
    return false;
  }
}
